import { LoaderCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { CookieNotice } from "./components/CookieNotice";
import { AccountPanel } from "./components/AccountPanel";
import { AdminPage } from "./components/AdminPage";
import { CreditShop } from "./components/CreditShop";
import { Footer } from "./components/Footer";
import { GeneratorPage } from "./components/GeneratorPage";
import { Header } from "./components/Header";
import { LegalPage } from "./components/LegalPage";
import { checkCheckout, loadConfig } from "./lib/api";
import type { PublicConfig } from "./types";

const legalRoutes = new Set(["privacy", "termini", "cookie", "regole", "assistenza"]);

export default function App() {
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [loadError, setLoadError] = useState("");
  const [shopOpen, setShopOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [checkoutNotice, setCheckoutNotice] = useState("");

  const page = useMemo(() => window.location.pathname.replace(/^\//, ""), []);

  useEffect(() => {
    loadConfig()
      .then(setConfig)
      .catch((reason) =>
        setLoadError(
          reason instanceof Error
            ? reason.message
            : "Il server non risponde. Avvialo e ricarica la pagina.",
        ),
      );
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get("checkout");
    const sessionId = params.get("session_id");
    if (checkout === "cancelled") {
      setCheckoutNotice("Acquisto annullato: nessun addebito, nessun dramma.");
      window.history.replaceState({}, "", "/");
    }
    if (checkout === "success" && sessionId) {
      setCheckoutNotice("Pagamento ricevuto. Sto contando i VAFFA…");
      checkCheckout(sessionId)
        .then((result) => {
          setConfig((current) =>
            current ? { ...current, credits: result.credits } : current,
          );
          setCheckoutNotice(
            result.paid
              ? `Fatto: il portafoglio ora contiene ${result.credits} VAFFA.`
              : "Il pagamento è ancora in elaborazione. Ricarica tra poco.",
          );
          window.history.replaceState({}, "", "/");
        })
        .catch((reason) => {
          setCheckoutNotice(
            reason instanceof Error
              ? reason.message
              : "Pagamento ricevuto, accredito in verifica.",
          );
        });
    }
  }, []);

  if (loadError) {
    return (
      <div className="boot-screen boot-screen--error">
        <h1>Vaffangoogle</h1>
        <p>{loadError}</p>
        <button type="button" onClick={() => window.location.reload()}>
          Riprova
        </button>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="boot-screen">
        <LoaderCircle className="spin" />
        <strong>Accendo il megafono…</strong>
      </div>
    );
  }

  const legalPage = legalRoutes.has(page) ? page : null;
  const isAdminPage = page === "admin";
  const openShop = () => {
    if (!config.profile) {
      setAccountOpen(true);
      return;
    }
    setShopOpen(true);
  };
  return (
    <div className={`app-shell ${isAdminPage ? "app-shell--admin" : ""}`}>
      {isAdminPage ? null : (
        <Header
          credits={config.credits}
          hasProfile={Boolean(config.profile)}
          onBuy={openShop}
          onProfile={() => setAccountOpen(true)}
        />
      )}
      {isAdminPage ? (
        <AdminPage config={config} onProfile={() => setAccountOpen(true)} />
      ) : legalPage ? (
        <LegalPage
          kind={legalPage as "privacy" | "termini" | "cookie" | "regole" | "assistenza"}
          config={config}
        />
      ) : (
        <GeneratorPage
          config={config}
          checkoutNotice={checkoutNotice}
          onCreditsChange={(credits) =>
            setConfig((current) => (current ? { ...current, credits } : current))
          }
          onBuy={openShop}
          onProfile={() => setAccountOpen(true)}
        />
      )}
      {isAdminPage ? null : <Footer supportUrl={config.supportUrl} />}
      <CookieNotice />
      <CreditShop
        config={config}
        open={shopOpen}
        onClose={() => setShopOpen(false)}
      />
      <AccountPanel
        config={config}
        open={accountOpen}
        onClose={() => setAccountOpen(false)}
        onChange={(change) =>
          setConfig((current) => current ? { ...current, ...change } : current)
        }
        onBuy={() => setShopOpen(true)}
      />
    </div>
  );
}
